<template>
  <div>
    <div class="flex relative flex-col w-full min-h-screen bg-white">
      <Header :page="{ isHome: false }" />

      <div
        v-if="event"
        class="container grid-main"
      >
        <TitleWithBackButton
          class="col-span-12 mt-6"
          :title="event.title"
          :backLabel="$t('event-detail-page-back-to-events')"
          :back-url="eventPageFullPath || '/'"
        />

        <!-- Event image -->
        <img
          class="col-span-12 max-h-[calc(100vh-(theme(grid.gridXMargin)))] object-cover"
          :src="event.images.list[0].publicUrlForCrop"
          :width="event.images.list[0].properties.dimensions.width"
          :height="event.images.list[0].properties.dimensions.height"
          loading="lazy"
        />

        <!-- Start date & Start time -->
        <div class="col-span-12 border-b-[0.5px] py-8 border-[#93A1AF]">
          <div class="flex justify-between items-center">
            <p>{{ $text('event-detail-page-date') }}</p>
            <template
              v-if="
                event.schedule &&
                event.schedule.length &&
                event.schedule[0].startDate &&
                event.schedule[0].endDate
              "
            >
              <div class="flex">
                <p>
                  {{ convertDateToEuropeanFormat(event.schedule[0].startDate) }}
                  -
                  {{ convertDateToEuropeanFormat(event.schedule[0].endDate) }}
                  <template v-if="event.schedule[0].startTime || event.schedule[0].endTime">
                    ,
                    <template v-if="event.schedule[0].startTime">{{
                      event.schedule[0].startTime
                    }}</template>
                    <template v-if="event.schedule[0].endTime">
                      - {{ event.schedule[0].endTime }}
                    </template>
                  </template>
                </p>
              </div>
            </template>
            <!-- Support fallback, including start and end times for cleanDates if available -->
            <template v-else>
              <p v-if="event.textFirstDate">
                {{ event.textFirstDate }}
                <template
                  v-if="
                    event.cleanDates &&
                    event.cleanDates.length &&
                    (event.cleanDates[0].startTime || event.cleanDates[0].endTime)
                  "
                >
                  ,
                  <template v-if="event.cleanDates[0].startTime">{{
                    event.cleanDates[0].startTime
                  }}</template>
                  <template v-if="event.cleanDates[0].endTime">
                    - {{ event.cleanDates[0].endTime }}
                  </template>
                </template>
                <template v-else-if="startTime"> , {{ startTime }} </template>
              </p>
              <p v-else>
                {{ convertDateToEuropeanFormat(startDate as string) }}
                <template
                  v-if="
                    event.cleanDates &&
                    event.cleanDates.length &&
                    (event.cleanDates[0].startTime || event.cleanDates[0].endTime)
                  "
                >
                  ,
                  <template v-if="event.cleanDates[0].startTime">{{
                    event.cleanDates[0].startTime
                  }}</template>
                  <template v-if="event.cleanDates[0].endTime">
                    - {{ event.cleanDates[0].endTime }}
                  </template>
                </template>
                <template v-else> , {{ startTime }} </template>
              </p>
            </template>
          </div>
        </div>

        <!-- Summary -->
        <div class="col-span-12 grid-main py-8 border-b-[0.5px] border-[#93A1AF]">
          <p class="col-span-9 mb-6 paragraph-1 lead">{{ event.summary }}</p>
          <div
            class="col-span-12 lg:col-span-9 markdown-render"
            v-html="$mdRenderer.render(event.description)"
          />
        </div>

        <!-- Price -->
        <div
          v-if="event.prices[0]"
          class="col-span-12"
        >
          <div class="flex border-b-[0.5px] py-8 border-[#93A1AF]">
            <p class="w-1/3 lg:w-[500px] md:w-1/2 mr-2">{{ $text('event-detail-page-price') }}</p>
            <div class="flex flex-col flex-wrap gap-2 w-2/3 md:w-1/2">
              <div
                v-for="(price, idx) in event.prices"
                :key="idx"
              >
                <div class="flex flex-row flex-wrap gap-x-2 w-full">
                  <p
                    class="w-full md:w-auto"
                    v-if="price?.labelPrice"
                  >
                    {{ price.labelPrice }}
                  </p>
                  <p
                    class="w-full md:w-auto"
                    v-if="price?.title"
                  >
                    {{ price.title }}
                  </p>
                  <p
                    class="w-full md:w-1/2"
                    v-if="price?.priceType === 'free'"
                  >
                    {{ $t('event-detail-page-free-price') }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Info location -->
        <div
          v-if="event.infoLocation"
          class="col-span-12"
        >
          <div class="flex py-8">
            <p class="w-1/3 lg:w-[500px] md:w-1/2 mr-2">
              {{ $text('event-detail-page-info-location') }}
            </p>

            <div class="w-2/3 md:w-1/2">
              <p>{{ event.infoLocation }}</p>
              <NuxtLink
                v-if="event?.otherCoordinates?.latitude && event?.otherCoordinates?.longitude"
                target="_BLANK"
                :to="`https://www.google.com/maps?q=${event.otherCoordinates.latitude},${event.otherCoordinates.longitude}`"
              >
                <ArrowLink
                  class="arrowLink"
                  :text="$text('google-directions-button') || 'Google Maps'"
                  :tag="'p'"
              /></NuxtLink>
            </div>
          </div>
          <div class="w-full border-b-[0.5px] border-[#93A1AF]" />
        </div>

        <!-- Info location -->
        <div
          v-if="Array.isArray(event.files) && event.files.length > 0"
          class="col-span-12"
        >
          <div class="flex border-b-[0.5px] py-8 border-[#93A1AF]">
            <p class="w-1/3 lg:w-[500px] md:w-1/2 mr-2">{{ $t('event-detail-page-downloads') }}</p>
            <div class="flex flex-wrap gap-2">
              <Button
                v-for="file in event.files"
                :key="file.url"
                :to="file.url"
                target="_blank"
                :arrowRight="true"
              >
                <template v-if="file.title || file.url">
                  {{ file.title || file.url }}
                  <template
                    v-if="file.extension"
                    class="ml-1 text-[10px] uppercase text-gray-400"
                    >.{{ file.extension }}</template
                  >
                </template>
              </Button>
            </div>
          </div>
        </div>

        <div
          v-if="
            (Array.isArray(event.links) &&
              event.links.length > 0 &&
              event.links.some((link) => link && link.url && link.title)) ||
            (Array.isArray(event.eventUrls) &&
              event.eventUrls.length > 0 &&
              event.eventUrls.some((link) => link && link.url))
          "
          class="col-span-12"
        >
          <div class="flex border-b-[0.5px] py-8 border-[#93A1AF]">
            <p class="w-1/3 lg:w-[500px] md:w-1/2 mr-2">{{ $t('event-detail-page-links') }}</p>

            <div class="flex flex-wrap gap-2">
              <template v-if="event.links.length > 0">
                <Button
                  v-for="link in event.links"
                  :to="link.url"
                  target="_blank"
                  :arrowRight="true"
                >
                  {{ link.title }}
                </Button>
              </template>

              <template v-if="event.eventUrls.length > 0">
                <Button
                  v-for="link in event.eventUrls"
                  :to="link.url"
                  target="_blank"
                  :arrowRight="true"
                >
                  {{ $t('event-detail-page-more-information') }}
                </Button>
              </template>
            </div>
          </div>
        </div>

        <!-- Info contact -->
        <div
          v-if="mergedContacts.length > 0"
          class="col-span-12"
        >
          <div class="flex flex-wrap py-8 md:flex-nowrap">
            <p class="w-full lg:w-[500px] md:w-1/2 mr-2 mb-6 md:mb-0">
              {{ $text('event-detail-page-contact') }}
            </p>

            <div class="flex flex-wrap w-full lg:flex-1">
              <div
                v-for="(contact, idx) in mergedContacts"
                :key="idx"
                class="flex flex-col pr-2 mb-4 w-full md:w-1/2"
              >
                <p class="mb-2 text-sm text-gray-500 uppercase">
                  {{ contact.labels.join(' / ') }}
                </p>
                <p class="font-bold">{{ contact.data.company }}</p>
                <p v-if="contact.data.region">{{ contact.data.region }}</p>
                <p v-if="contact.data.street">{{ contact.data.street }}</p>
                <p v-if="contact.data.zipcode || contact.data.city || contact.data.country">
                  <template v-if="contact.data.zipcode">{{ contact.data.zipcode }}</template>
                  <template v-if="contact.data.city"
                    >{{ contact.data.zipcode ? ' ' : '' }}{{ contact.data.city }}</template
                  >
                  <template v-if="contact.data.country"> - {{ contact.data.country }}</template>
                </p>
                <a
                  :href="`tel:${contact.data.phone}`"
                  v-if="contact.data.phone"
                  >{{ contact.data.phone }}</a
                >
                <a
                  :href="`tel:${contact.data.mobile}`"
                  v-if="contact.data.mobile"
                  >{{ contact.data.mobile }}</a
                >
                <a
                  v-if="contact.data.email"
                  class="w-full cursor-pointer"
                  :href="`mailto:${contact.data.email}`"
                  >{{ contact.data.email }}</a
                >
                <a
                  v-if="contact.data.website"
                  target="_BLANK"
                  class="w-full cursor-pointer"
                  :href="`${contact.data.website}`"
                  >{{ contact.data.website }}</a
                >
              </div>
            </div>
          </div>
          <div class="w-full border-b-[0.5px] border-[#93A1AF]" />
        </div>
        <!-- Default Waldhaus location -->
        <div
          class="col-span-12"
          v-else
        >
          <div class="flex items-center py-8">
            <p class="w-1/3 lg:w-[500px] md:w-1/2 mr-2">{{ $text('event-detail-page-contact') }}</p>

            <div class="flex flex-col waldhaus-contact">
              -
              <!-- <SanityContent :blocks="siteParts?.contactInfo?.contactText" /> -->
            </div>
          </div>
          <div class="w-full border-b-[0.5px] border-[#93A1AF]" />
        </div>
      </div>

      <!-- Fallback if anything goes wrong with the external API -->
      <div
        class="container mt-8"
        v-else
      >
        <TitleWithBackButton
          class="mt-6"
          :title="''"
          :backLabel="'zurück zur Veranstaltungsübersicht'"
          :back-url="eventPageFullPath || '/'"
        />
        <h3 class="mb-2">Attention!</h3>
        <p>There was an issue with our Event System API. We are invastigating on the issue.</p>
        <pre>page: {{ event }}</pre>
        <pre>event: {{ event }}</pre>
      </div>
      <Footer />
    </div>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { useSettingsStore } from '~/stores/useSettingsStore';
import { ContentItem } from 'types';

// Props
interface EventPageProps {
  page: {
    _type: string;
    event: ContentItem;
  };
}
const { page } = defineProps<EventPageProps>();

// Stores
const settingsStore = useSettingsStore();
const { siteParts } = storeToRefs(settingsStore);

// Composables
const { convertDateToEuropeanFormat } = useSiteSettings();
const { $fullpath, $text } = useSiteSettings();
const {
  query: { startDate, startTime }
} = useRoute();

// Refs
const event = page.event?.data?.item;
const eventPageFullPath = siteParts.value && $fullpath(siteParts.value?.general?.eventPage?._ref);

// Computed property to merge duplicate contacts
const mergedContacts = computed(() => {
  if (!event) return [];

  const { t } = useI18n();

  const contactTypes = [
    {
      type: 'infoContact',
      label: t('event-detail-page-contact-info'),
      data: event.infoContact
    },
    {
      type: 'organizerContact',
      label: t('event-detail-page-contact-organizer'),
      data: event.organizerContact
    },
    {
      type: 'eventContact',
      label: t('event-detail-page-contact-event'),
      data: event.eventContact
    },
    {
      type: 'bookingContact',
      label: t('event-detail-page-contact-booking'),
      data: event.bookingContact
    }
  ];

  // Filter out empty contacts
  const validContacts = contactTypes.filter(
    (ct) =>
      ct.data &&
      (ct.data.company ||
        ct.data.region ||
        ct.data.street ||
        ct.data.zipcode ||
        ct.data.city ||
        ct.data.country ||
        ct.data.phone ||
        ct.data.mobile ||
        ct.data.email ||
        ct.data.website)
  );

  // Group contacts by their data
  const grouped: any[] = [];

  validContacts.forEach((contact) => {
    // Helper function to normalize values (treat null, undefined, and empty string as equivalent)
    const normalize = (val: any) => val || '';

    // Check if we already have this contact data
    const existing = grouped.find((g) => {
      const c1 = g.data;
      const c2 = contact.data;
      return (
        normalize(c1.company) === normalize(c2.company) &&
        normalize(c1.region) === normalize(c2.region) &&
        normalize(c1.street) === normalize(c2.street) &&
        normalize(c1.zipcode) === normalize(c2.zipcode) &&
        normalize(c1.city) === normalize(c2.city) &&
        normalize(c1.country) === normalize(c2.country) &&
        normalize(c1.phone) === normalize(c2.phone) &&
        normalize(c1.mobile) === normalize(c2.mobile) &&
        normalize(c1.email) === normalize(c2.email) &&
        normalize(c1.website) === normalize(c2.website)
      );
    });

    if (existing) {
      // Add label to existing contact
      existing.labels.push(contact.label);
    } else {
      // Create new grouped contact
      grouped.push({
        data: contact.data,
        labels: [contact.label]
      });
    }
  });

  return grouped;
});
</script>

<style lang="postcss">
.waldhaus-contact {
  p {
    @apply text-black not-italic;
    font-size: fluid(18, 18);
    line-height: fluid(23.8, 28);
    font-weight: normal;
    white-space: pre-line;
  }
}
.markdown-render {
  p:not(:first-child),
  p * {
    @apply py-1;
    font-size: fluid(18, 18);
    line-height: fluid(23.8, 23.8);
  }
  p:first-child {
    @apply mb-4;
  }
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    @apply my-4;
  }

  ul,
  ol {
    @apply ml-3 mb-7;

    p {
      @apply !mb-0;
    }
  }

  p {
    /* @apply border-b-[0.2px] border-[#93A1AF]; */
  }
}
</style>
